import { EMPTY_ARRAY } from 'constants/defaults';
import { HOUR_EDITOR_MAX_SUGGESTIONS } from 'constants/hour';
import { CLIENT_FOCUS_ON_PROJECTS_BY_DEFAULT } from 'constants/user';
import { isEqual, uniq } from 'lodash-es';
import { getTicTasks } from 'redux/modules/tic/tasks/tic.tasks.slice';
import {
  getAccountCurrency,
  getAllProjectsLoggingEnabled,
  getCanUseClientFocusOnProjects,
} from 'redux/selectors/accounts';
import { getClients } from 'redux/selectors/clients';
import {
  getHourDraftEntryIds,
  getHourDraftExternalLinks,
  getHourDraftForecastId,
  getHourDraftProjectId,
  getHourDraftShowAllProjectsForDraft,
  getHourDraftUserId,
} from 'redux/selectors/hours.draft.base';
import {
  getAllLoadedProjects,
  getAllProjectsHaveBeenFetched,
  getPinnedProjectsForProjectDropdown,
  getProjectPermissionsMap,
  getProjectVariantMap,
  getProjectXhrStatus,
  getRecentlyUsedPinnedProjectIdsSet,
} from 'redux/selectors/projects';
import { getEntryIdPredictions } from 'redux/selectors/timeline';
import { getUser, getUserId, getUserPermissions, getUserProperties } from 'redux/selectors/user';
import type { IState } from 'redux/state';
import { createSelector, lruMemoize } from 'reselect';
import { getForecastsByState } from 'routes/forecast/redux/modules/forecast.selectors';
import { filterForecastListForUser } from 'routes/forecast/utils/forecast';
import type { HourEditorProjectSectionProps } from 'types/hour';
import type { EnrichedProject } from 'types/project';
import type { SuggestedEntryProject } from 'types/timeline';
import { getActiveProjects, getAvailableProjectsForUser } from 'utils/project';
import { notNull } from 'utils/typesafe';
import { getCalendarUserId } from './calendar.filter';
import { resolveClientFocusOnProjects } from './hours.draft.client-focus-on-projects';
import { getPinnedProjectIdsSet } from './pins';
import {
  enrichProjectVariant,
  enrichProjectVariantOrNull,
  projectSelectorFilterProjectIds,
} from './utils/projects';

/**
 * Returns project ids for suggestions and "recent projects" optgroup
 * for the user we're acting as on an HourDraft
 */
export const getRecentProjectsIdsForHourDraft = createSelector(
  [getProjectPermissionsMap, getProjectVariantMap, getHourDraftUserId, getUserId],
  (permissionMap, variantMap, hourDraftUserId, authenticatedUserId): Array<number> => {
    const permissions = permissionMap[hourDraftUserId ?? authenticatedUserId] ?? [];
    return projectSelectorFilterProjectIds(permissions, variantMap);
  }
);

export const getRecentProjectsForHourDraft = createSelector(
  [getRecentProjectsIdsForHourDraft, getProjectVariantMap, getRecentlyUsedPinnedProjectIdsSet],
  (projectIds, projectById, recentlyUsedPinnedProjectIdsSet): Array<EnrichedProject> => {
    return projectIds
      .filter(projectId => !recentlyUsedPinnedProjectIdsSet.has(projectId))
      .map(projectId => {
        return enrichProjectVariantOrNull(projectById[projectId], recentlyUsedPinnedProjectIdsSet);
      })
      .filter(notNull);
  }
);

export const getSuggestedProjectsForHourDraft = createSelector(
  [
    getRecentProjectsIdsForHourDraft,
    getProjectVariantMap,
    getHourDraftEntryIds,
    getEntryIdPredictions,
    getPinnedProjectIdsSet,
  ],
  (
    recentProjectIds,
    projectVariantMap,
    selectedEntryIds,
    entryIdPredictions,
    pinnedProjectIdsSet
  ): Array<SuggestedEntryProject> => {
    // Order the entry ids by the predicted project's accuracy
    const predictedProjectIds = selectedEntryIds
      .filter(entryId => entryIdPredictions[entryId] && entryIdPredictions[entryId].accuracy > 0)
      .sort((a, b) => {
        return entryIdPredictions[b].accuracy - entryIdPredictions[a].accuracy;
      })
      .flatMap(entryId => entryIdPredictions[entryId].project_id);

    // Return all suggestion candidates. The display cap is applied after client focus
    // narrowing so scoped projects are not accidentally sliced out first.
    const suggestedProjects = uniq([...predictedProjectIds, ...recentProjectIds])
      .map(projectId => {
        const variant = projectVariantMap[projectId];
        if (!variant) {
          return null;
        }
        return {
          ...enrichProjectVariant(variant, pinnedProjectIdsSet),
          accuracy: 0,
        };
      })
      .filter(notNull);

    return suggestedProjects.length ? suggestedProjects : EMPTY_ARRAY;
  },
  { memoizeOptions: { resultEqualityCheck: isEqual } }
);

const getShouldUseClientFocusOnProjectsByDefault = createSelector(
  [getUserProperties],
  properties => properties[CLIENT_FOCUS_ON_PROJECTS_BY_DEFAULT] !== false
);

const projectBelongsToClient = (project: EnrichedProject, clientId: number) =>
  project.base.client.id === clientId;

const filterProjectsToClient = <T extends EnrichedProject>(
  projects: Array<T>,
  clientId: number | null
) => {
  if (clientId == null) {
    return projects;
  }
  return projects.filter(project => projectBelongsToClient(project, clientId));
};

const projectsAsSuggestions = (projects: Array<EnrichedProject>): Array<SuggestedEntryProject> =>
  projects.map(project => ({ ...project, accuracy: 0 }));

export const getPermissionsForDraftOwner = createSelector(
  [getHourDraftUserId, getProjectPermissionsMap],
  (userId, permissions) => permissions[userId!] ?? EMPTY_ARRAY
);

/**
 * The set of project ids the hour-draft owner is a member of. This is the
 * single source of truth for membership: `insertInferredProjectPermissions`
 * is what populates it (and it deliberately does NOT add a project when the
 * user only logged to it once under ALL_PROJECTS_LOGGING).
 */
export const getDraftOwnerMemberProjectIdSet = createSelector(
  [getPermissionsForDraftOwner],
  permissions => new Set(permissions)
);

/**
 * Whether the draft owner is currently a member of `projectId`. Used by the
 * save flow to decide whether logging to the project needs the "join project"
 * confirmation dialog (ALL_PROJECTS_LOGGING). A project explicitly flagged
 * `has_permission === false` is treated as non-member even if a stale entry
 * lingers in the permission list.
 */
export const getDraftOwnerIsMemberOfProject = (state: IState, projectId: number): boolean => {
  if (getDraftOwnerMemberProjectIdSet(state).has(projectId)) {
    const variant = getProjectVariantMap(state)[projectId];
    const hasPermission =
      variant?.type === 'light' ? variant.lightProject.has_permission : undefined;
    return hasPermission !== false;
  }
  return false;
};

// NB: This is more or less a direct copy of selectors/projects::projectsForUser
// This method could use getProjectEntities and a similar structure for permissions
// Maybe re-write and loop over permissions instead of projects?
export const getProjectsForDraftUserCore = createSelector(
  [
    getAllLoadedProjects,
    getClients,
    getPermissionsForDraftOwner,
    getHourDraftProjectId,
    getAllProjectsLoggingEnabled,
  ],
  (projects, clients, permissions, hourDraftProjectId, allProjectsLogging) => {
    const archivedClientIds = clients.filter(client => !client.active).map(client => client.id);

    // When ALL_PROJECTS_LOGGING is on, the hour-editor picker should list
    // every active workspace project regardless of which projects the user
    // is currently a member of. Use the dedicated O(n) active-projects
    // helper instead of synthesizing a permission list (which would force
    // an O(n*m) includes() check per project). Backend auto-joins on save.
    if (allProjectsLogging) {
      const activeProjects = getActiveProjects(projects, archivedClientIds);
      if (hourDraftProjectId && !activeProjects.some(p => p.base.id === hourDraftProjectId)) {
        const selected = projects.find(p => p.base.id === hourDraftProjectId);
        if (selected) {
          return [...activeProjects, selected];
        }
      }
      return activeProjects;
    }

    return getAvailableProjectsForUser(
      projects,
      false,
      archivedClientIds,
      permissions,
      hourDraftProjectId ? [hourDraftProjectId] : []
    );
  }
);

/**
 * Wraps `getProjectsForDraftUserCore` with deep equality memoization, to avoid producing a new
 * `projects` instance when `hourDraftProjectId` changes but the same projects are included.
 */
export const getProjectsForDraftUser = createSelector(
  [getProjectsForDraftUserCore],
  projects => projects,
  { memoize: lruMemoize, memoizeOptions: { equalityCheck: isEqual } }
);

/**
 * Get forecasts assigned to the owner of the hour draft
 */
export const getForecastsForDraftUser = createSelector(
  [getForecastsByState, getCalendarUserId, getHourDraftForecastId],
  ({ active }, actingUserId, forecastId) => {
    if (!actingUserId) {
      return [];
    }
    return active.filter(forecast => {
      const forecastIsLinkedToDraft = forecast.id === forecastId;
      return (
        filterForecastListForUser(forecast, {
          userId: actingUserId,
          excludeCompletedForecasts: !forecastIsLinkedToDraft,
        }) || forecastIsLinkedToDraft
      );
    });
  }
);

export const getHourEditorProjectSectionProps = createSelector(
  [
    getAccountCurrency,
    getProjectsForDraftUser,
    getRecentProjectsForHourDraft,
    getPinnedProjectsForProjectDropdown,
    getSuggestedProjectsForHourDraft,
    getCanUseClientFocusOnProjects,
    getShouldUseClientFocusOnProjectsByDefault,
    getHourDraftShowAllProjectsForDraft,
    getHourDraftExternalLinks,
    getTicTasks,
    getUser,
    getUserPermissions,
    getHourDraftUserId,
    getHourDraftProjectId,
    getClients,
    getProjectXhrStatus,
    getAllProjectsHaveBeenFetched,
    getForecastsForDraftUser,
  ],
  (
    currency,
    projects,
    recentProjects,
    pinnedProjects,
    suggestedProjects,
    canUseClientFocusOnProjects,
    shouldUseClientFocusOnProjects,
    showAllProjectsForDraft,
    externalLinks,
    tasks,
    user,
    userPermissions,
    actingUserId,
    selectedProjectId,
    clients,
    { isLoading },
    allProjectsLoaded,
    forecasts
  ): HourEditorProjectSectionProps => {
    const effectiveUserId = actingUserId ?? user.id;
    const isActingAsOtherUser = actingUserId !== user.id;
    const clientFocusOnProjects = resolveClientFocusOnProjects({
      canUseClientFocusOnProjects,
      shouldUseClientFocusOnProjects,
      showAllProjectsForDraft,
      externalLinks,
      tasks,
      clients,
    });
    const focusedClientId = clientFocusOnProjects.isActive ? clientFocusOnProjects.clientId : null;
    const scopedProjects = filterProjectsToClient(projects, focusedClientId);
    const scopedRecentProjects = focusedClientId == null ? recentProjects : EMPTY_ARRAY;
    const scopedPinnedProjects = focusedClientId == null ? pinnedProjects : EMPTY_ARRAY;
    const filteredSuggestedProjects = filterProjectsToClient(suggestedProjects, focusedClientId);
    const scopedSuggestedProjects =
      filteredSuggestedProjects.length > 0 || focusedClientId == null
        ? filteredSuggestedProjects
        : projectsAsSuggestions(scopedProjects);
    const scopedForecasts =
      focusedClientId == null
        ? forecasts
        : forecasts.filter(forecast => forecast.project?.client.id === focusedClientId);
    const scopedClients =
      focusedClientId == null ? clients : clients.filter(client => client.id === focusedClientId);
    const hasForecasts = scopedForecasts.length > 0;

    // Determine how many forecasts or projects should be suggested:
    const showForecastSuggestions = hasForecasts && !selectedProjectId;
    const numForecastSuggestions = Math.min(scopedForecasts.length, HOUR_EDITOR_MAX_SUGGESTIONS);
    const hasRoomForProjectSuggestions = numForecastSuggestions < HOUR_EDITOR_MAX_SUGGESTIONS;
    const showProjectSuggestions = hasRoomForProjectSuggestions && !selectedProjectId;
    const numProjectSuggestions = Math.min(
      scopedSuggestedProjects.length,
      HOUR_EDITOR_MAX_SUGGESTIONS - numForecastSuggestions
    );

    return {
      projects: scopedProjects,
      recentProjects: scopedRecentProjects,
      pinnedProjects: scopedPinnedProjects,
      suggestions: scopedSuggestedProjects,
      forecasts: scopedForecasts,
      clients: scopedClients,
      currency,
      userId: effectiveUserId,
      ui: {
        isActingAsOtherUser,
        showForecastSuggestions,
        numForecastSuggestions,
        showProjectSuggestions,
        numProjectSuggestions,
        selectedProjectId,
        allowCreateProject: userPermissions.canCreate.projects,
        isLoading,
        placeholder: showForecastSuggestions
          ? 'Select Task or Project'
          : showProjectSuggestions
            ? 'Select Project'
            : hasForecasts
              ? 'Search Client, Project or Task'
              : 'Search Client or Project',
        allProjectsLoaded,
        clientFocusOnProjects,
      },
    };
  }
);
